import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, IconButton, Pagination, Slider, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import '../css/AvailableProfiles.css';
import API_BASE_URL from '../api';

const ITEMS_PER_PAGE = 8;

const AvailableProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [availability, setAvailability] = useState('');
  const [experience, setExperience] = useState('');
  const [educationLevel, setEducationLevel] = useState('');
  const [maxTJM, setMaxTJM] = useState(1800);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/profils`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const publishedProfiles = response.data.filter(profile => profile.status === 'published');
        const sortedProfiles = publishedProfiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setProfiles(sortedProfiles);
        setFilteredProfiles(sortedProfiles);

        const skills = new Set();
        sortedProfiles.forEach(profile => {
          profile.skills.forEach(skill => skills.add(skill));
        });

        const skillsOptions = Array.from(skills).map(skill => ({ value: skill, label: skill }));
        setSkillsOptions(skillsOptions);
      } catch (error) {
        setError('Erreur lors de la récupération des profils');
      }
    };

    fetchProfiles();
  }, []);

  const handleSkillChange = (selectedOptions) => {
    filterProfiles({ skills: selectedOptions.map(option => option.value) });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    filterProfiles({ searchTerm: e.target.value });
  };

  const handleAvailabilityChange = (e) => {
    setAvailability(e.target.value);
    filterProfiles({ availability: e.target.value });
  };

  const handleExperienceChange = (e) => {
    setExperience(e.target.value);
    filterProfiles({ experience: e.target.value });
  };

  const handleEducationLevelChange = (e) => {
    setEducationLevel(e.target.value);
    filterProfiles({ educationLevel: e.target.value });
  };

  const handleTJMChange = (event, newValue) => {
    setMaxTJM(newValue);
    filterProfiles({ maxTJM: newValue });
  };

  const filterProfiles = (filter = {}) => {
    let updatedProfiles = profiles;

    if (filter.skills) {
      updatedProfiles = updatedProfiles.filter(profile => filter.skills.every(skill => profile.skills.includes(skill)));
    }

    if (filter.searchTerm) {
      updatedProfiles = updatedProfiles.filter(profile => profile.title.toLowerCase().includes(filter.searchTerm.toLowerCase()));
    }

    if (filter.availability) {
      updatedProfiles = updatedProfiles.filter(profile => new Date(profile.availability) >= new Date(filter.availability));
    }

    if (filter.experience) {
      updatedProfiles = updatedProfiles.filter(profile => profile.experience === filter.experience);
    }

    if (filter.educationLevel) {
      updatedProfiles = updatedProfiles.filter(profile => profile.educationLevel === filter.educationLevel);
    }

    if (filter.maxTJM) {
      updatedProfiles = updatedProfiles.filter(profile => profile.budgetTJM <= filter.maxTJM);
    }

    setFilteredProfiles(updatedProfiles);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentProfiles = filteredProfiles.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Box className="available-profiles-container" sx={{ display: 'flex' }}>
      <Box className="sidebar" sx={{ width: '250px', bgcolor: 'white', p: 2, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Navigation
        </Typography>
        <Button component={Link} to="/profiles" fullWidth variant="contained" color="primary" sx={{ mb: 2 }}>
          Profils disponibles
        </Button>
        <Button component={Link} to="/missions-list" fullWidth variant="contained" color="secondary">
          Besoins disponibles
        </Button>
        <Typography variant="h6" gutterBottom mt={4}>
          Filtres de recherche
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel>Disponibilité</InputLabel>
          <Select value={availability} onChange={handleAvailabilityChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Immediate">Immédiate</MenuItem>
            <MenuItem value="1 week">1 semaine</MenuItem>
            <MenuItem value="1 month">1 mois</MenuItem>
            <MenuItem value="3 months">3 mois</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Expérience</InputLabel>
          <Select value={experience} onChange={handleExperienceChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Moins de 2 ans">Moins de 2 ans</MenuItem>
            <MenuItem value="2 à 5 ans">2 à 5 ans</MenuItem>
            <MenuItem value="5 à 10 ans">5 à 10 ans</MenuItem>
            <MenuItem value="Plus de 10 ans">Plus de 10 ans</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Niveau d'études</InputLabel>
          <Select value={educationLevel} onChange={handleEducationLevelChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Bac">Bac</MenuItem>
            <MenuItem value="Bac+2">Bac+2</MenuItem>
            <MenuItem value="Bac+3">Bac+3</MenuItem>
            <MenuItem value="Bac+5">Bac+5</MenuItem>
            <MenuItem value="Bac+8">Bac+8</MenuItem>
          </Select>
        </FormControl>
        <Typography gutterBottom>Tarification TJM max</Typography>
        <Slider
          value={maxTJM}
          onChange={handleTJMChange}
          aria-labelledby="tarification-slider"
          step={50}
          min={0}
          max={1800}
          valueLabelDisplay="auto"
        />
        <Typography gutterBottom>Compétences</Typography>
        <CreatableSelect
          isMulti
          options={skillsOptions}
          onChange={handleSkillChange}
          placeholder="Rechercher par compétences"
          className="skills-select"
        />
      </Box>
      <Box className="main-content" sx={{ flex: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {filteredProfiles.length} Profils disponibles
        </Typography>
        <Box className="controls" sx={{ display: 'flex', mb: 3, gap: 2 }}>
          <TextField 
            variant="outlined" 
            placeholder="Rechercher des profils" 
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              )
            }}
            fullWidth
          />
        </Box>
        {error && <Typography color="error">{error}</Typography>}
        <TableContainer component={Paper}>
          <Table className="profile-table">
            <TableHead>
              <TableRow>
                <TableCell>Réf.</TableCell>
                <TableCell>Titre</TableCell>
                <TableCell>TJM</TableCell>
                <TableCell>Disponibilité</TableCell>
                <TableCell>Mobilité</TableCell>
                <TableCell>Publié le</TableCell>
                <TableCell>-</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentProfiles.map((profile) => (
                <TableRow key={profile._id}>
                  <TableCell>{profile.reference}</TableCell>
                  <TableCell>{profile.title}</TableCell>
                  <TableCell>{`${profile.budgetTJM}€`}</TableCell>
                  <TableCell>{new Date(profile.availability).toLocaleDateString()}</TableCell>
                  <TableCell>{profile.location}</TableCell>
                  <TableCell>{new Date(profile.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button component={Link} to={`/profils/${profile._id}`} variant="contained" color="primary">
                      Détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredProfiles.length / ITEMS_PER_PAGE)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AvailableProfiles;
